import { useEffect, useRef, useState } from 'react';

import {
  deliveryZoneError,
  DeliveryZoneImportReport,
  DeliveryZoneRow,
  importAdminDeliveryZones,
  listAdminDeliveryZones,
  listBrandDeliveryZones,
  previewBrandDeliveryFee,
  previewAdminDeliveryFee,
  saveAdminDeliveryZone,
  ZoneCheckAddress,
  ZoneCheckResult,
} from '@shared/api/giftDeliveryZone/giftDeliveryZone.admin';
import { GiftDeliveryZonePolicy } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import DaumPostcode from 'react-daum-postcode';
import { useQuery } from 'react-query';

import {
  downloadDeliveryZones,
  readDeliveryZoneFile,
} from '../utils/giftDeliveryZoneExcel';

const input = 'rounded border border-gray-300 bg-white px-2 py-1.5 text-sm';
const button =
  'rounded border border-gray-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40';
const emptyRow = () => ({
  sido: '',
  sigungu: '',
  emd: '',
  extraFee: '0',
  deliverable: true,
});

export function GiftDeliveryZoneManager({
  brandId,
  getPolicy,
  policyRevision,
  readOnly = false,
  brandSeller = false,
}: {
  readOnly?: boolean;
  brandSeller?: boolean;
  brandId: string;
  getPolicy: () => GiftDeliveryZonePolicy | undefined;
  policyRevision: string;
}) {
  const [q, setQ] = useState('');
  const [sido, setSido] = useState('');
  const [status, setStatus] = useState('all');
  const [minExtraFee, setMinExtraFee] = useState('');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [edit, setEdit] = useState<ReturnType<typeof emptyRow> | null>(null);
  const [editingExisting, setEditingExisting] = useState(false);
  const [upload, setUpload] = useState<{ name: string; rows: DeliveryZoneRow[] } | null>(
    null,
  );
  const [retireMissing, setRetireMissing] = useState(false);
  const [restoreDisabled, setRestoreDisabled] = useState(false);
  const [report, setReport] = useState<DeliveryZoneImportReport | null>(null);
  const [address, setAddress] = useState<ZoneCheckAddress | null>(null);
  const [addressLabel, setAddressLabel] = useState('');
  const [showAddress, setShowAddress] = useState(false);
  const [subtotal, setSubtotal] = useState('');
  const [checked, setChecked] = useState<ZoneCheckResult | null>(null);
  const checkGeneration = useRef(0);
  const params = {
    giftItemBrand: brandId,
    q,
    sido,
    deliverable: status,
    minExtraFee,
    skip: page * 50,
    limit: 50,
  };
  const query = useQuery(
    ['deliveryZones', brandSeller, params],
    () => (brandSeller ? listBrandDeliveryZones : listAdminDeliveryZones)(params),
    { keepPreviousData: true },
  );
  useEffect(() => {
    checkGeneration.current++;
    setChecked(null);
  }, [policyRevision, address, subtotal]);
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await action();
    } catch (e) {
      setError(deliveryZoneError(e));
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    checkGeneration.current++;
    setChecked(null);
    setReport(null);
    await query.refetch();
  };
  const previewImport = () =>
    run(async () => {
      if (!upload) return;
      setReport(null);
      setReport(
        await importAdminDeliveryZones({
          giftItemBrand: brandId,
          rows: upload.rows,
          file: upload.name,
          retireMissing,
          restoreDisabled,
          dryRun: true,
        }),
      );
    });
  const applyImport = () =>
    run(async () => {
      if (!upload || !report?.valid) return;
      // A failed apply must be reviewed again, including any writes completed before a DB error.
      setReport(null);
      const result = await importAdminDeliveryZones({
        giftItemBrand: brandId,
        rows: upload.rows,
        file: upload.name,
        retireMissing,
        restoreDisabled,
        dryRun: false,
      });
      setUpload(null);
      await refresh();
      setMessage(
        `반영 완료: 신규 ${result.created}곳, 변경 ${result.changed}곳, 목록에서 빠져 배송불가 ${result.retired}곳`,
      );
    });
  const exportList = () =>
    run(async () => {
      const rows: DeliveryZoneRow[] = [];
      for (let skip = 0; ; skip += 500) {
        const result = await (brandSeller
          ? listBrandDeliveryZones
          : listAdminDeliveryZones)({ ...params, skip, limit: 500 });
        rows.push(...result.items);
        if (rows.length >= result.count || result.items.length === 0) break;
      }
      downloadDeliveryZones(rows);
      setMessage(`현재 검색 결과 ${rows.length}곳을 내려받았습니다.`);
    });
  const saveRow = () =>
    run(async () => {
      if (!edit) return;
      const fee = Number(edit.extraFee);
      if (!edit.extraFee.trim() || !Number.isSafeInteger(fee) || fee < 0)
        throw new Error('추가배송비는 0 이상의 정수로 입력해 주세요.');
      await saveAdminDeliveryZone(brandId, { ...edit, extraFee: fee });
      setEdit(null);
      await refresh();
      setMessage('지역을 저장했습니다.');
    });
  const checkFee = () =>
    run(async () => {
      setChecked(null);
      const policy = getPolicy();
      if (!policy?.enabled) throw new Error('지역별 배송비 사용을 선택해 주세요.');
      if (!address) throw new Error('배송지를 검색해 주세요.');
      if (
        !subtotal.trim() ||
        !Number.isSafeInteger(Number(subtotal)) ||
        Number(subtotal) < 0
      )
        throw new Error('상품금액을 입력해 주세요.');
      const generation = ++checkGeneration.current;
      const result = await (brandSeller
        ? previewBrandDeliveryFee
        : previewAdminDeliveryFee)({
        giftItemBrand: brandId,
        ...(!readOnly ? { policy } : {}),
        address,
        subtotal: Number(subtotal),
      });
      if (checkGeneration.current === generation) setChecked(result);
    });
  return (
    <div
      className="space-y-5 rounded-lg border border-gray-200 p-4"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement)
          event.preventDefault();
      }}
    >
      <div>
        <h3 className="font-semibold">{readOnly ? '배송지역 조회' : '배송지역 관리'}</h3>
        <p className="mt-1 text-xs text-gray-500">
          {readOnly
            ? '등록된 지역별 추가배송비와 배송가능 여부를 조회합니다. 변경은 관리자에게 문의해 주세요.'
            : '지역 변경은 저장·반영 버튼을 누르면 즉시 적용됩니다. 브랜드 요금은 페이지 하단에서 저장하세요.'}
        </p>
        {query.data && (
          <p className="mt-2 text-sm">
            전체 {query.data.summary.total.toLocaleString()}곳 · 배송가능{' '}
            {query.data.summary.enabled.toLocaleString()}곳 · 배송불가{' '}
            {query.data.summary.disabled.toLocaleString()}곳
          </p>
        )}
      </div>
      {error && (
        <p role="alert" className="whitespace-pre-wrap text-sm text-red-600">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="text-sm text-green-700">
          {message}
        </p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-red-600">
          지역 목록을 불러오지 못했습니다. {deliveryZoneError(query.error)}{' '}
          <button type="button" className={button} onClick={() => void query.refetch()}>
            다시 시도
          </button>
        </p>
      )}
      <fieldset disabled={busy} className="space-y-4">
        {!readOnly && (
          <div className="space-y-2 rounded bg-gray-50 p-3">
            <h4 className="text-sm font-semibold">엑셀 일괄등록</h4>
            <p className="text-xs text-gray-600">
              양식의 배송지역 시트에 작성하세요. 기본은 추가·수정이며, 기존 배송불가
              지역은 그대로 유지합니다.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={button}
                onClick={() => downloadDeliveryZones()}
              >
                빈 양식 다운로드
              </button>
              <input
                aria-label="배송지역 엑셀 업로드"
                type="file"
                accept=".xlsx"
                className="max-w-full text-sm"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  setReport(null);
                  setUpload(null);
                  if (file)
                    void run(async () => {
                      const rows = await readDeliveryZoneFile(file);
                      setUpload({ name: file.name, rows });
                    });
                }}
              />
            </div>
            {upload && (
              <p className="text-sm">
                {upload.name} · {upload.rows.length.toLocaleString()}행
              </p>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={retireMissing}
                onChange={(e) => {
                  setRetireMissing(e.target.checked);
                  setReport(null);
                }}
              />
              파일에 없는 기존 지역은 배송불가로 전환
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={restoreDisabled}
                onChange={(e) => {
                  setRestoreDisabled(e.target.checked);
                  setReport(null);
                }}
              />
              기존 배송불가 지역도 파일에 ‘가능’이면 다시 허용
            </label>
            <button
              type="button"
              className={button}
              disabled={!upload}
              onClick={() => void previewImport()}
            >
              등록 전 검토
            </button>
            {report && (
              <div
                className="space-y-2 rounded border bg-white p-3 text-sm"
                role="status"
              >
                <p>
                  고유 지역 {report.keys}곳 · 동일 지역 중복 {report.duplicates}행
                </p>
                {report.valid ? (
                  <>
                    <p>
                      신규 {report.created}곳 · 변경 {report.changed}곳 · 동일{' '}
                      {report.unchanged}곳
                    </p>
                    <p>
                      파일 내 배송불가 {report.disabled}곳 (기존 차단 유지{' '}
                      {report.preservedDisabled}곳 포함)
                    </p>
                    <p className={report.retired ? 'font-semibold text-red-600' : ''}>
                      파일에서 빠져 배송불가로 전환: {report.retired}곳
                    </p>
                    <button
                      type="button"
                      className={button}
                      onClick={() => void applyImport()}
                    >
                      검토한 내용 반영
                    </button>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-red-600">
                      오류 {report.errors.length}건을 수정해야 반영할 수 있습니다.
                    </p>
                    <ul className="max-h-48 overflow-auto text-red-600">
                      {report.errors.map((e, i) => (
                        <li key={i}>
                          {e.row ? `${e.row}행: ` : ''}
                          {e.message}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <input
            aria-label="지역 검색"
            className={input}
            placeholder="시도·시군구·읍면동 검색"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
          />
          <select
            aria-label="시도 필터"
            className={input}
            value={sido}
            onChange={(e) => {
              setSido(e.target.value);
              setPage(0);
            }}
          >
            <option value="">전체 시도</option>
            {query.data?.sidos.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            aria-label="배송가능 필터"
            className={input}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
          >
            <option value="all">전체 상태</option>
            <option value="true">배송가능</option>
            <option value="false">배송불가</option>
          </select>
          <input
            aria-label="최소 추가배송비"
            type="number"
            min={0}
            step={1}
            className={`${input} w-40`}
            placeholder="추가배송비 이상(원)"
            value={minExtraFee}
            onChange={(e) => {
              setMinExtraFee(e.target.value);
              setPage(0);
            }}
          />
          {!readOnly && (
            <button
              type="button"
              className={button}
              onClick={() => {
                setEdit(emptyRow());
                setEditingExisting(false);
              }}
            >
              지역 추가
            </button>
          )}
          <button
            type="button"
            className={button}
            disabled={query.isFetching || query.isError}
            onClick={() => void exportList()}
          >
            검색 결과 다운로드
          </button>
        </div>
        {!readOnly && edit && (
          <div className="space-y-2 rounded border border-indigo-200 p-3">
            <p className="text-sm font-semibold">
              {editingExisting ? '지역 수정' : '지역 추가'}
            </p>
            <div className="flex flex-wrap gap-2">
              {(['sido', 'sigungu', 'emd'] as const).map((field, i) => (
                <input
                  key={field}
                  aria-label={['지역 시도', '지역 시군구', '지역 읍면동'][i]}
                  disabled={editingExisting}
                  className={`${input} w-36`}
                  placeholder={['시도', '시군구', '읍면동'][i]}
                  value={edit[field]}
                  onChange={(e) => setEdit({ ...edit, [field]: e.target.value })}
                />
              ))}
              <input
                aria-label="지역 추가배송비"
                type="number"
                min={0}
                step={1}
                className={`${input} w-32`}
                value={edit.extraFee}
                onChange={(e) => setEdit({ ...edit, extraFee: e.target.value })}
              />
              <select
                aria-label="지역 배송가능 여부"
                className={input}
                value={String(edit.deliverable)}
                onChange={(e) =>
                  setEdit({ ...edit, deliverable: e.target.value === 'true' })
                }
              >
                <option value="true">배송가능</option>
                <option value="false">배송불가</option>
              </select>
              <button type="button" className={button} onClick={() => void saveRow()}>
                지역 저장
              </button>
              <button type="button" className={button} onClick={() => setEdit(null)}>
                닫기
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-left text-sm">
            <thead>
              <tr>
                {[
                  '시도',
                  '시군구',
                  '읍면동',
                  '추가배송비',
                  '상태',
                  ...(!readOnly ? ['관리'] : []),
                ].map((h) => (
                  <th key={h} className="border-b p-2">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {query.data?.items.map((row) => (
                <tr key={row._id}>
                  <td className="border-b p-2">{row.sido}</td>
                  <td className="border-b p-2">{row.sigungu || '—'}</td>
                  <td className="border-b p-2">{row.emd}</td>
                  <td className="border-b p-2">{row.extraFee.toLocaleString()}원</td>
                  <td
                    className={`border-b p-2 ${
                      row.deliverable === false ? 'text-red-600' : ''
                    }`}
                  >
                    {row.deliverable === false ? '배송불가' : '배송가능'}
                  </td>
                  {!readOnly && (
                    <td className="border-b p-2">
                      <button
                        type="button"
                        className={button}
                        disabled={query.isFetching}
                        onClick={() => {
                          setEdit({
                            sido: row.sido,
                            sigungu: row.sigungu,
                            emd: row.emd,
                            extraFee: String(row.extraFee),
                            deliverable: row.deliverable !== false,
                          });
                          setEditingExisting(true);
                        }}
                      >
                        수정
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {query.isFetching ? (
            <p className="py-3 text-sm">지역을 불러오는 중입니다.</p>
          ) : (
            !query.isError &&
            !query.data?.items.length && (
              <p className="py-3 text-sm text-gray-500">조건에 맞는 지역이 없습니다.</p>
            )
          )}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button
            type="button"
            className={button}
            disabled={page === 0 || query.isFetching}
            onClick={() => setPage(page - 1)}
          >
            이전
          </button>
          <span>
            {page + 1} / {Math.max(1, Math.ceil((query.data?.count ?? 0) / 50))} 페이지 ·{' '}
            {query.data?.count ?? 0}곳
          </span>
          <button
            type="button"
            className={button}
            disabled={query.isFetching || (page + 1) * 50 >= (query.data?.count ?? 0)}
            onClick={() => setPage(page + 1)}
          >
            다음
          </button>
        </div>
        <div className="space-y-2 border-t pt-4">
          <h4 className="text-sm font-semibold">주소별 배송비 확인</h4>
          <p className="text-xs text-gray-500">
            {readOnly
              ? '저장된 브랜드 요금과 배송지역으로 계산합니다.'
              : '화면에 입력한 브랜드 요금과 저장된 배송지역으로 계산합니다.'}{' '}
            상품금액은 해당 브랜드의 옵션 추가금 포함·배송비 제외 금액입니다.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={button}
              onClick={() => setShowAddress(!showAddress)}
            >
              주소 검색
            </button>
            <span className="text-sm">{addressLabel}</span>
            <input
              aria-label="확인할 상품금액"
              type="number"
              min={0}
              step={1}
              placeholder="상품금액(원)"
              className={input}
              value={subtotal}
              onChange={(e) => setSubtotal(e.target.value)}
            />
            <button type="button" className={button} onClick={() => void checkFee()}>
              배송비 계산
            </button>
          </div>
          {showAddress && (
            <DaumPostcode
              style={{ height: 400 }}
              onComplete={(data) => {
                setAddress({
                  sido: data.sido,
                  sigungu: data.sigungu,
                  bname: data.bname,
                  bname1: data.bname1,
                  hname: data.hname,
                });
                setAddressLabel(data.address);
                setShowAddress(false);
              }}
            />
          )}
          {checked && (
            <div
              role="status"
              className={`rounded p-3 text-sm ${
                checked.deliverable ? 'bg-green-50' : 'bg-red-50'
              }`}
            >
              {checked.deliverable ? (
                <>
                  <p>
                    배송가능 · 기본배송비 {checked.baseFee.toLocaleString()}원 + 지역
                    추가배송비 {checked.extraFee.toLocaleString()}원 ={' '}
                    <b>{checked.total.toLocaleString()}원</b>
                  </p>
                  <p>
                    기본배송비 면제금액: {checked.rule?.freeOverAmount.toLocaleString()}원
                  </p>
                  {checked.zone && (
                    <p>
                      적용 지역: {checked.zone.sido} {checked.zone.sigungu}{' '}
                      {checked.zone.emd}
                    </p>
                  )}
                </>
              ) : (
                <p>
                  배송불가 —{' '}
                  {checked.reason === 'ADDRESS_INCOMPLETE'
                    ? '주소를 다시 검색해 주세요.'
                    : checked.reason === 'ZONE_DISABLED'
                    ? '배송불가로 설정된 지역입니다.'
                    : '등록되지 않은 지역입니다.'}
                </p>
              )}
            </div>
          )}
        </div>
      </fieldset>
      {busy && (
        <p role="status" className="text-sm text-indigo-600">
          처리 중입니다.
        </p>
      )}
    </div>
  );
}
